import { copy } from './clipboard.js';
import { announce } from './announce.js';
import { registerMasks } from './mask.js';

const emit = (el, ...types) => types.forEach((type) => el.dispatchEvent(new Event(type, { bubbles: true })));

/**
 * Delegated behaviors for form controls. Everything hangs off document, so it keeps
 * working after Livewire morphs or wire:navigate swaps the page.
 */
export function registerFormBehaviors(t) {
    const syncMasks = registerMasks(t);

    document.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-mellow-input-clear], [data-mellow-input-copy], [data-mellow-input-view]');
        if (!button) return;

        const input = button.closest('.mellow-input')?.querySelector('.mellow-input-control');
        if (!input) return;

        if (button.hasAttribute('data-mellow-input-clear')) {
            input.value = '';
            emit(input, 'input', 'change');
            input.focus();
        }

        if (button.hasAttribute('data-mellow-input-copy')) {
            if (await copy(input.value)) {
                button.setAttribute('data-done', '');
                announce(t('copied'));
                setTimeout(() => button.removeAttribute('data-done'), 2000);
            } else {
                // Select it, so copying by hand is one shortcut away.
                input.select();
                announce(t('copy_failed'));
            }
        }

        if (button.hasAttribute('data-mellow-input-view')) {
            const visible = input.type === 'password';
            input.type = visible ? 'text' : 'password';
            button.setAttribute('aria-pressed', String(visible));
            button.setAttribute('aria-label', visible ? button.dataset.labelHide : button.dataset.labelShow);
        }
    });

    // Clicking the input chrome (padding, icons) focuses the input.
    document.addEventListener('mousedown', (event) => {
        const wrapper = event.target.closest('.mellow-input');
        if (!wrapper || event.target.closest('input, button, a')) return;
        event.preventDefault();
        wrapper.querySelector('.mellow-input-control')?.focus();
    });

    // "Select all" checkboxes inside a checkbox group.
    let bulkUpdate = false;
    document.addEventListener('change', (event) => {
        const checkbox = event.target;
        if (!(checkbox instanceof HTMLInputElement) || checkbox.type !== 'checkbox') return;

        const group = checkbox.closest('.mellow-choice-group');
        if (!group) return;

        if (bulkUpdate) return;

        if (checkbox.hasAttribute('data-mellow-check-all')) {
            // Freeze the target state: the events we emit below would otherwise re-sync "all" mid-loop.
            const checked = checkbox.checked;
            bulkUpdate = true;
            try {
                items(group).forEach((item) => {
                    if (item.checked !== checked && !item.disabled) {
                        item.checked = checked;
                        emit(item, 'input', 'change');
                    }
                });
            } finally {
                bulkUpdate = false;
            }
        }

        syncCheckAll(group);
    });

    // Drag & drop state for file dropzones (dropping itself is handled natively by the file input).
    let dragDepth = 0;
    document.addEventListener('dragenter', (event) => {
        const zone = event.target.closest?.('[data-mellow-dropzone]');
        if (zone) {
            dragDepth++;
            zone.setAttribute('data-dragging', '');
        }
    });
    const endDrag = (event) => {
        const zone = event.target.closest?.('[data-mellow-dropzone]');
        if (!zone) return;
        if (event.type === 'drop' || --dragDepth <= 0) {
            dragDepth = 0;
            zone.removeAttribute('data-dragging');
        }
    };
    document.addEventListener('dragleave', endDrag);
    document.addEventListener('drop', endDrag);

    // Livewire upload progress.
    const progress = (event) => {
        const zone = event.target.closest?.('[data-mellow-dropzone]');
        const box = zone?.querySelector('.mellow-file-upload-progress');
        if (!box) return;

        const value = event.type === 'livewire-upload-progress' ? event.detail.progress : 0;
        box.hidden = !['livewire-upload-start', 'livewire-upload-progress'].includes(event.type);
        zone.toggleAttribute('data-uploading', !box.hidden);
        box.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', value);
        box.querySelector('.mellow-progress-bar').style.width = `${value}%`;
    };
    ['livewire-upload-start', 'livewire-upload-progress', 'livewire-upload-finish', 'livewire-upload-error', 'livewire-upload-cancel']
        .forEach((type) => document.addEventListener(type, progress));

    const textareaAutosize = !CSS.supports('field-sizing', 'content');
    if (textareaAutosize) {
        document.addEventListener('input', (event) => {
            if (event.target.matches?.('textarea[data-autosize]')) resize(event.target);
        });
    }

    // Custom elements that re-apply derived state (ARIA, filters…) after Livewire morphs.
    const SYNCED = 'mellow-dropdown, mellow-tooltip, mellow-modal, mellow-select, mellow-sidebar, mellow-autocomplete, mellow-date-picker, mellow-chart, mellow-color-picker, mellow-carousel, mellow-status-history';

    // Properties that can't be expressed as HTML attributes (indeterminate, autosize, check-all state).
    const scan = (root = document) => {
        root.querySelectorAll?.('[data-indeterminate]').forEach((el) => (el.indeterminate = true));
        root.querySelectorAll?.(SYNCED).forEach((el) => el.sync?.());
        root.querySelectorAll?.('.mellow-choice-group').forEach(syncCheckAll);
        syncMasks(root);
        if (textareaAutosize) root.querySelectorAll?.('textarea[data-autosize]').forEach(resize);
    };

    // Livewire sets checked states and values as properties, which no observer sees: rescan after each update.
    const hookLivewire = () => window.Livewire.hook('commit', ({ succeed }) => succeed(() => requestAnimationFrame(() => scan())));
    if (window.Livewire) hookLivewire();
    else document.addEventListener('livewire:init', hookLivewire);
    document.addEventListener('livewire:initialized', () => requestAnimationFrame(() => scan()));

    onReady(() => {
        scan();
        new MutationObserver((mutations) => {
            const owners = new Set();
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((node) => node.nodeType === 1 && scan(node.parentElement ?? node));
                // Children changed (e.g. new options from the server): let the owning element update.
                const owner = mutation.target.closest?.(SYNCED);
                if (owner) owners.add(owner);
            }
            owners.forEach((owner) => owner.sync?.());
        }).observe(document.body, { childList: true, subtree: true });
    });
}

function items(group) {
    return [...group.querySelectorAll('input[type="checkbox"]:not([data-mellow-check-all])')];
}

function syncCheckAll(group) {
    const all = group.querySelector('[data-mellow-check-all]');
    if (!all) return;

    const boxes = items(group);
    const checked = boxes.filter((box) => box.checked).length;
    all.checked = checked > 0 && checked === boxes.length;
    all.indeterminate = checked > 0 && checked < boxes.length;
}

function resize(textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight + 2}px`;
}

function onReady(callback) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback);
    } else {
        callback();
    }
}
